import React from 'react';

import { Alert, Checkbox, DatePicker, Form, Input, message, Modal } from 'antd';
import type { RuleObject } from 'antd/es/form';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { ErrorCode } from '@app/models/error';
import { RetrievePoaSchema } from '@app/models/users';
import { hasErrorCode } from '@app/services/apiErrors';
import { getFormErrorsFromResponse } from '@app/services/getFormErrorsFromResponse';
import { useApiErrorMessage } from '@app/services/hooks/useApiErrorMessage';

import { useCreatePoaMutation, useUpdatePoaMutation } from '../../../../hooks/usePoaQueries';
import { ValidationSchema } from '../../data/validation';
import { IFormValues, IPoaTarget } from '../../interfaces';
import { formatSnils } from '../../services';

interface IProps {
  open: boolean;
  target: IPoaTarget;
  poa?: RetrievePoaSchema;
  onClose: () => void;
}

export const PoaModal: React.FC<IProps> = ({ open, target, poa, onClose }) => {
  const [form] = Form.useForm<IFormValues>();
  const [formError, setFormError] = React.useState<string>();
  const spaceT = useTranslation(NSKeys.spaceSettings);
  const commonT = useTranslation(NSKeys.common);
  const validationT = useTranslation(NSKeys.validation);
  const getErrorMessage = useApiErrorMessage();
  const create = useCreatePoaMutation(target);
  const update = useUpdatePoaMutation(target);
  const busy = create.isLoading || update.isLoading;

  React.useEffect(() => {
    if (open) {
      form.resetFields();
      if (poa) {
        form.setFieldsValue({ ...poa, representative_snils: formatSnils(poa.representative_snils) });
      }
      setFormError(undefined);
    }
  }, [open, poa, form]);

  const handleSubmit = (values: IFormValues) => {
    setFormError(undefined);
    const data = {
      number: values.number,
      internal_number: values.internal_number?.trim() || null,
      issued_on: values.issued_on,
      valid_until: values.valid_until,
      representative_last_name: values.representative_last_name.trim(),
      representative_first_name: values.representative_first_name.trim(),
      representative_middle_name: values.representative_middle_name?.trim() || null,
      representative_inn: values.representative_inn,
      representative_snils: values.representative_snils.replace(/[\s-]/g, ''),
      representative_position: values.representative_position?.trim() || null,
      ...(values.is_primary && !poa?.is_primary ? { is_primary: true as const } : {}),
    };
    const callbacks = {
      onSuccess: () => {
        message.success(commonT.t('dataPatchSuccess'));
        onClose();
      },
      onError: (err: unknown) => {
        const { pointerErrors } = getFormErrorsFromResponse(values, err, validationT.t);
        if (hasErrorCode(err, [ErrorCode.EPD_POA_PERIOD_INVALID])) {
          form.setFields([{ name: 'valid_until', errors: [getErrorMessage(err)] }]);
        } else if (pointerErrors.length) {
          form.setFields(pointerErrors);
        } else {
          setFormError(getErrorMessage(err));
        }
      },
    };
    if (poa) {
      update.mutate({ uid: poa.uid, data }, callbacks);
    } else {
      create.mutate(data, callbacks);
    }
  };

  const yupSync = {
    async validator(rule: RuleObject, value: unknown) {
      const { field } = rule as RuleObject & { field: string };
      await ValidationSchema(validationT.t).validateSyncAt(field, { ...form.getFieldsValue(), [field]: value });
    },
  };

  return (
    <Modal
      open={open}
      title={spaceT.t(poa ? 'poa.edit' : 'poa.add')}
      okText={commonT.t('buttonSave')}
      cancelText={commonT.t('buttonCancel')}
      onOk={() => form.submit()}
      onCancel={busy ? undefined : onClose}
      closable={!busy}
      cancelButtonProps={{ disabled: busy }}
      confirmLoading={busy}
      destroyOnHidden
    >
      <Form<IFormValues>
        form={form}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        onFinish={handleSubmit}
        disabled={busy}
        initialValues={{ is_primary: false }}
      >
        <Form.Item name="number" label={spaceT.t('poa.number')} required rules={[yupSync]}>
          <Input />
        </Form.Item>
        <Form.Item name="internal_number" label={spaceT.t('poa.internalNumber')} rules={[yupSync]}>
          <Input />
        </Form.Item>
        <Form.Item name="representative_last_name" label={spaceT.t('poa.lastName')} required rules={[yupSync]}>
          <Input />
        </Form.Item>
        <Form.Item name="representative_first_name" label={spaceT.t('poa.firstName')} required rules={[yupSync]}>
          <Input />
        </Form.Item>
        <Form.Item name="representative_middle_name" label={spaceT.t('poa.middleName')} rules={[yupSync]}>
          <Input />
        </Form.Item>
        <Form.Item name="representative_inn" label={commonT.t('inn')} required rules={[yupSync]}>
          <Input inputMode="numeric" maxLength={12} />
        </Form.Item>
        <Form.Item
          name="representative_snils"
          label={spaceT.t('poa.snils')}
          required
          rules={[yupSync]}
          normalize={formatSnils}
        >
          <Input inputMode="numeric" />
        </Form.Item>
        <Form.Item name="representative_position" label={spaceT.t('poa.position')} rules={[yupSync]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="issued_on"
          label={spaceT.t('poa.issuedOn')}
          required
          rules={[yupSync]}
          normalize={(value) => (value ? value.format('YYYY-MM-DD') : undefined)}
          getValueProps={(value) => ({ value: value ? dayjs(value) : null })}
        >
          <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="valid_until"
          label={spaceT.t('poa.validUntil')}
          required
          rules={[yupSync]}
          dependencies={['issued_on']}
          normalize={(value) => (value ? value.format('YYYY-MM-DD') : undefined)}
          getValueProps={(value) => ({ value: value ? dayjs(value) : null })}
        >
          <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
        </Form.Item>
        {!poa?.is_primary && (
          <Form.Item name="is_primary" valuePropName="checked" wrapperCol={{ offset: 8, span: 16 }}>
            <Checkbox>{spaceT.t('poa.makePrimary')}</Checkbox>
          </Form.Item>
        )}
        {formError && <Alert type="error" showIcon title={formError} />}
      </Form>
    </Modal>
  );
};
